import { Widget } from "@typeform/embed-react";
import { useParams, useSearchParams } from "react-router-dom";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DssContext } from "../../shared/providers/dss/dss.provider";
import { IDSSForm, IFormValues } from "../../shared/interfaces/dss.interface";
import { DSS_SECTION, FORM_TYPE } from "../../shared/constants";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { delay, formatDate, formatTemplate, getFormattedPath, markdownToHtml } from "../../shared/helpers/utils";
import Link from "../../components/Link/Link";
import { routes } from "../../shared/router/routes";
import Button from "../../components/Button/Button";
import { UserContext } from "../../shared/providers/user/user.provider";
import Modal from "../../components/Modal/Modal";
import { useGetForm } from "../../shared/hooks/useTypeform";
import { LayoutContext } from "../../shared/providers/layout/layout.provider";

const DssForm = () => {
    const { lang, translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { user } = useContext(UserContext);
    const { forms, updateForms } = useContext(DssContext);
    const { setActions } = useContext(LayoutContext);
    const { formId } = useParams();
    const [query] = useSearchParams();

    const [form, setForm] = useState<IDSSForm>();
    const [value, setValue] = useState<number | undefined>(0);
    const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
    const [currentQuestion, setCurrentQuestion] = useState<number>(1);
    const hiddenFormProps = useMemo(() => ({ user_id: user?.id ?? "" }), [user]);
    const { data: formQuestions } = useGetForm<[string]>(formId ?? "", { enabled: !!formId });

    useEffect(() => {
        setActions(<Link
            to={routes.DSS.path}
            icon="Chevron--left"
            iconSize={16}
            customCss="p-0 d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
            {translate("common.back", "Back")}
        </Link>)

        return () => {
            setActions(undefined)
        }
    }, [translate]);


    const nextForm = useMemo(() => {
        if (!form) return null;

        // FIND ALL FORMS FROM THE SAME SECTION THAT ARE NOT ASSESSED
        const sectionForms = forms.filter((f) => f.section === form.section && (!f.values || f.values.length === 0));

        // GET THE CURRENT FORM INDEX
        const index = sectionForms.findIndex((f) => f.formId === form?.formId);

        // RETURN THE NEXT FORM IF THERE IS ANY
        return sectionForms[index + 1] ?? undefined;
    }, [form, forms]);

    useEffect(() => {
        const form = forms.find((form) => form.formId === formId);
        if (!form) return;

        setForm(form);

        if (query.get("reassess") === "true") setValue(undefined);
        else {
            setIsSuccess(form.values?.length ? true : undefined);
            setValue(form.values?.length ? form.values[0].score : undefined);
        }
    }, [forms, formId]);

    const handleOnReady = useCallback(() => {
        setCurrentQuestion(1);
    }, [setCurrentQuestion]);

    const handleQuestionChange = useCallback((e: { ref: string }) => {
        setCurrentQuestion((formQuestions?.indexOf(e.ref) ?? 0) + 1);
    }, [formQuestions]);

    const handleSubmit = useCallback(async (event: { formId: string; responseId: string }) => {
        if (!lang) return;

        setIsLoading(true);

        try {
            await delay(2000);
            const success = await updateForms();
            if (success) setIsSuccess(true);
            else setIsSuccess(false);
        } catch (e) {
            setIsSuccess(false);
        }

        setIsLoading(false);
    }, [setIsLoading, setIsSuccess, updateForms]);

    const scoreClass = useMemo(() => {
        const [lastSubmission] = form?.values ?? [];
        const value = lastSubmission?.score;

        if (value === undefined)
            return "";
        if (value > 79)
            return "is_green";
        if (value > 59)
            return "is_warning";
        if (value >= 0)
            return "is_red";

        return "";
    }, [form]);

    const feedback = useMemo(() => {
        if (!form) return "";

        const [lastSubmission] = form.values ?? [];
        const value = lastSubmission?.score;

        if (value === undefined) return "";

        if (value > 79) return form.feedback_80;
        if (value > 59) return form.feedback_60;
        if (value >= 0) return form.feedback_0;

        return "";
    }, [form]);

    const content = useMemo(() => {
        if (!form) return "";

        const [lastSubmission] = form.values ?? [];
        const value = lastSubmission?.score;

        if (value === undefined) return "";

        if (value > 79) return form.content_80;
        if (value > 59) return form.content_60;
        if (value >= 0) return form.content_0;

        return "";
    }, [form]);

    if (!formId) return <></>;

    return (
        <div className="d-flex flex-column gap-23 mb-23">
            <section aria-labelledby="title">
                <span className="d-flex align-items-center gap-02 text-uppercase text-bd2-bd color-blue-500 mb-03">
                    <i className="icon icon-24 icon-neurology" aria-hidden="true" />
                    {form?.section === DSS_SECTION.MENTAL
                        ? translate("dss.mental_health", "Mental health")
                        : translate("dss.physical_health", "Physical health")}
                </span>
                <h2 id="title" className="text-h1-bd mb-03">
                    {form?.title}
                </h2>

                {isSuccess === undefined && (<div className="typeform_wrapper d-flex flex-column gap-1">
                    <div className="d-flex gap-2 align-items-center">
                        <progress
                            className="card_progress flex-grow-1"
                            value={(currentQuestion) / (formQuestions?.length ?? 1)} max="1">
                        </progress>
                        <span className="text-bd4-md color-neutrals-neutral flex-shrink-0">
                            {`${currentQuestion}/${formQuestions?.length} ${translate("common.questions", "questions")}`}
                        </span>
                    </div>
                    <Widget
                        inlineOnMobile
                        id={`${formId}`}
                        hidden={hiddenFormProps}
                        className="typeform flex-grow-1"
                        onSubmit={handleSubmit}
                        onQuestionChanged={handleQuestionChange}
                        onReady={handleOnReady}
                    /></div>
                )}

                {isSuccess && (
                    <div className="endscreen is_success px-11 py-2 p-md-4 pe-md-31 d-flex gap-22 gap-md-53 align-items-start mt-11">
                        <span className="order-3 d-md-none text-bd4-bd color-neutrals-dark text-uppercase ">
                            {translate("common.last_updated", "Last updated")}: {formatDate(new Date(), "dd MMMM YYYY")}
                        </span>
                        <div className="d-flex flex-column gap-1 gap-md-03 align-items-start text-start">
                            <span className="d-none d-md-block text-bd4-bd color-neutrals-dark text-uppercase">
                                {translate("common.last_updated", "Last updated")}: {formatDate(new Date(), "dd MMMM YYYY")}
                            </span>
                            <h3 className="text-h3-bd text-h1-bd-md">
                                {feedback}
                            </h3>
                            <p className="m-0 text-bd1-rg text-break">{content}</p>
                            <div className="d-flex flex-wrap flex-md-nowrap gap-12 mt-12 mt-md-13">
                                <Button
                                    type="button"
                                    variant="solid"
                                    onClick={() => setIsSuccess(undefined)}>
                                    {translate("dss.reassess_your_workplace", "Reassess your workplace")}
                                </Button>
                            </div>
                        </div>
                        <div className="score_wrapper">
                            <div className={`score ${scoreClass}`}>
                                <span
                                    className={`d-flex align-items-center justify-content-center color-neutrals-white`}>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {!isSuccess && isSuccess !== undefined && (
                    <div className="endscreen is_success px-11 py-2 p-md-4 d-flex gap-22 gap-md-53 align-items-center align-items-md-start mt-11">
                        <div className="d-flex flex-column gap-1 gap-md-03">
                            <h3 className="text-h3-bd text-h1-bd-md">Oops...</h3>
                            <p className="message m-0 text-bd1-rg">
                                {translate(
                                    "dds.error_message",
                                    "Something went wrong. We're working on resolving this, please try again later!"
                                )}
                            </p>
                        </div>
                        <div className="error">
                            <span className="d-flex align-items-center justify-content-center color-red-500">
                                <i className="icon icon-48 icon-info"></i>
                            </span>
                        </div>
                    </div>
                )}

                {!!nextForm && isSuccess !== undefined && (
                    <div className="category_next d-flex flex-column gap-03 mt-2">
                        <h3 className="text-h5-bd">
                            {translate("dss.assess_next_category", "Assess the next category")}
                        </h3>
                        <article className="d-flex justify-content-md-between align-items-center flex-column flex-md-row gap-3 gap-md-1 px-11 py-22 bg-neutrals-white">
                            <div className="d-flex gap-02 flex-column">
                                <span className="text-bd4-bd color-neutrals-dark text-uppercase">
                                    {translate("dss.no_assessment", "No assessment done yet")}
                                </span>
                                <h4 className="text-h4-md">{nextForm.title}</h4>
                            </div>
                            <Link to={getFormattedPath(routes.DSS_FORM.path, { formId: nextForm.formId })} variant="solid" customCss="px-21">
                                {translate("dss.start_assessment", "Start assessment")}
                            </Link>
                        </article>
                    </div>
                )}
            </section>
        </div>
    );
};

export default DssForm;
